import { Component, Input, Inject, Output, EventEmitter, PLATFORM_ID, forwardRef, ContentChild, TemplateRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { isPlatformBrowser } from '@angular/common';
import { LookupPopupComponent } from './lookupPopup.component';
import { of, Observable } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { AimsCommonTableHeader } from '@aims/common-components';

export const AIMS_LOOKUP_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LookupComponent),
  multi: true
};

/*
  When using <aims-lookup> within a table (aims-common-table) or editor popup (AimsCommonPopupEditorComponent)
  You can show it's invalid with : <aims-lookup ... [ngClass]="{'ng-invalid': formGroup.controls.equipmentId.invalid}">
  And mark the formControl as required, ex: FormControl('name', Validators.Required)
  Otherwise, it's "required" but nothing on the form will be red
*/


@Component({
  selector: 'aims-lookup',
  exportAs: 'aimsLookup',
  styleUrls: ['./lookup.component.css'],
  templateUrl: './lookup.component.html',
  providers: [AIMS_LOOKUP_CONTROL_VALUE_ACCESSOR]
})
export class LookupComponent implements ControlValueAccessor {


    @Input() name: string;
    @Input() title: string; // shown in control box
    @Input() required: boolean;
    @Input() values: Array<any> | Observable<Array<any>>;
    @Input() popupValues: Array<any> | Observable<Array<any>>;
    @Input() idPropertyName: string;
    @Input() displayPropertyName: string | Array<string>;
    @Input() columnsInGrid: AimsCommonTableHeader[] = [];
    @Input() columnsPreFilter: ColumnPrefilter[] = [];
    @Input() altDisplay: string = '';
    @Input() linkHref: string;
    @Input() gridText: string;
    @Input() note: string;
    @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() valueChangeObj: EventEmitter<any> = new EventEmitter<any>();
    @Output() popupOpened: EventEmitter<void> = new EventEmitter<void>();

    constructor(@Inject(PLATFORM_ID) private platformId: Object,
        public dialog: MatDialog) {
    }
  lookupClass:string;

  @ContentChild('content', { read: TemplateRef }) content: TemplateRef<any>;

  _value: number | string = '';
  selectedObj: any = null;

  //Handle links if enabled
  clickVal() {
    if (this.linkHref !== undefined && this._value != null && this._value != '' && isPlatformBrowser(this.platformId)) {
      window.open(this.linkHref, "_blank");
    }
    else {
      this.clickMagnifyingGlass();
    }
  }

  /** Value of the control. */
  @Input()
  get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    this.writeValue(newValue, true);
  }

  //ControlValueAccessor implementation
  writeValue(value: any, nullIt: boolean = false): void {
    //if (value || nullIt) {
    this._value = value;
    if (nullIt) {
      this.onChange(this._value);  //Have to manually call this since we aren't actually firing the onChnage event
    }
    //Pipe this through a delay to avoid problems with calling functions on valueChange in grid
    of(this._value).pipe(
      delay(0),
      take(1)
    ).subscribe(v =>
        this.valueChange.emit(v)
    );
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  onChange = (_) => {};

  registerOnTouched(fn: any): void {
  }

  _disabled: boolean = false;
  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled;
  }

  clickMagnifyingGlass() {
    if (!this._disabled) {
        let popup = this.dialog.open(LookupPopupComponent,{
            data: this.getValues(),
            disableClose: true
        });

        popup.componentInstance.lookup = this;
        popup.componentInstance._value = this._value;

        const selectedVal$ = popup.componentInstance.selectedVal.subscribe(v => {
            this.writeValue(v);
            //Need to test if function exists in case we didn't bind to ngModel
            if (this.onChange) {
                this.onChange(v); //change event doesn't fire when calling above
            }
            this.valueChange.emit(this._value);
        })

        const selectedObj$ = popup.componentInstance.selectedObj.subscribe(output => {
            this.selectedObj = output;
            this.valueChangeObj.emit(this.selectedObj);
        });

        popup.afterClosed().subscribe(ret => {
            selectedObj$.unsubscribe();
            selectedVal$.unsubscribe();
        });

        this.popupOpened.emit();
    }
  }

  ngOnInit() {
    if (this.displayPropertyName == undefined) {
      this.displayPropertyName = this.idPropertyName;
    }
    if(this.gridText == undefined){
      this.gridText = "Select An Item From The List Below";
    }
    this.lookupClass = 'lookupInput';
    if(this._disabled){
      this.lookupClass += ' disabledLookup';
    }
    if(this.linkHref !== undefined){
      this.lookupClass += ' isALink'
    }
  }

  getValues() {
    let retObs = of([]);
    if (this.popupValues !== undefined) {
      if (this.popupValues instanceof Observable) {
        retObs = this.popupValues;
      }
      else {
        retObs = of(this.popupValues);
      }
    }
    else (retObs = (this.values instanceof Observable) ? this.values : of(this.values))
    return retObs;
  }
}

export interface ColumnPrefilter {
    columnId: string;
    preFilterValue: string | Observable<string>;
}
